/* pc-product-reviews.js — Supabase-backed product reviews.
   Reads config from [data-pc-reviews] data-attributes. Fetches the summary +
   pages of reviews from PostgREST, renders summary/histogram/cards, and handles
   star-filter, sort, "show more", helpful votes (RPC + localStorage guard), and
   the write-a-review dialog (INSERT; rows land unpublished for moderation).
   All review content is rendered with textContent — never innerHTML. */
(function () {
  "use strict";

  var SORTS = {
    recent: "reviewed_at.desc,id.desc",
    helpful: "helpful_yes.desc,reviewed_at.desc",
    high: "rating.desc,reviewed_at.desc",
    low: "rating.asc,reviewed_at.desc"
  };
  var ATTR_LABELS = { exam_result: "Exam", study_weeks: "Study time", role: "Role" };

  function init(root) {
    var api = root.dataset.api ? root.dataset.api.replace(/\/+$/, "") : "";
    var key = root.dataset.key || "";
    var handle = root.dataset.handle || "";
    if (!api || !key || !handle) return;

    var state = {
      pageSize: parseInt(root.dataset.pageSize, 10) || 6,
      showEmpty: root.dataset.showEmpty === "true",
      sort: "recent",
      starFilter: null,
      offset: 0,
      summary: null,
      loading: false
    };

    var els = {
      summary: root.querySelector("[data-rev-summary]"),
      toolbar: root.querySelector("[data-rev-toolbar]"),
      filterNote: root.querySelector("[data-rev-filter-note]"),
      sort: root.querySelector("[data-rev-sort]"),
      list: root.querySelector("[data-rev-list]"),
      more: root.querySelector("[data-rev-more]"),
      moreBtn: root.querySelector(".js-rev-more"),
      empty: root.querySelector("[data-rev-empty]"),
      dialog: root.querySelector("[data-rev-dialog]"),
      form: root.querySelector("[data-rev-form]"),
      formBody: root.querySelector("[data-rev-form-body]"),
      formDone: root.querySelector("[data-rev-form-done]"),
      formError: root.querySelector("[data-rev-form-error]")
    };

    function rest(path) {
      return fetch(api + "/rest/v1/" + path, {
        headers: { apikey: key, Authorization: "Bearer " + key }
      }).then(function (r) {
        if (!r.ok) throw new Error("reviews fetch " + r.status);
        return r.json();
      });
    }

    /* ── SVG stars ── */
    var STAR_PATH = "M10 1.6l2.47 5.34 5.84.63-4.35 3.94 1.18 5.75L10 14.35l-5.14 2.91 1.18-5.75L1.69 7.57l5.84-.63L10 1.6z";
    function starRowSvg(color) {
      var s = "";
      for (var i = 0; i < 5; i++) {
        s += '<svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true"><path d="' + STAR_PATH + '" fill="' + color + '"/></svg>';
      }
      return s;
    }
    function stars(rating, label) {
      var wrap = document.createElement("span");
      wrap.className = "pc-rev__stars";
      wrap.setAttribute("role", "img");
      wrap.setAttribute("aria-label", label || "Rated " + rating + " out of 5 stars");
      wrap.innerHTML = starRowSvg("var(--pc-cream-alt)");
      var fill = document.createElement("span");
      fill.className = "pc-rev__stars-fill";
      fill.setAttribute("aria-hidden", "true");
      fill.style.width = Math.max(0, Math.min(100, (rating / 5) * 100)) + "%";
      fill.innerHTML = starRowSvg("var(--pc-yellow-deep)");
      wrap.appendChild(fill);
      return wrap;
    }

    /* ── Summary ── */
    function renderSummary(s) {
      els.summary.textContent = "";

      var avg = document.createElement("div");
      avg.className = "pc-rev__avg";
      var num = document.createElement("div");
      num.className = "pc-rev__avg-num";
      num.textContent = Number(s.avg_rating).toFixed(1);
      avg.appendChild(num);
      avg.appendChild(stars(Number(s.avg_rating), "Average rating " + Number(s.avg_rating).toFixed(1) + " out of 5 stars"));
      var count = document.createElement("div");
      count.className = "pc-rev__avg-count";
      count.textContent = "Based on " + s.review_count + (s.review_count === 1 ? " review" : " reviews");
      avg.appendChild(count);
      els.summary.appendChild(avg);

      var bars = document.createElement("div");
      bars.className = "pc-rev__bars";
      [5, 4, 3, 2, 1].forEach(function (n) {
        var c = s["r" + n] || 0;
        var bar = document.createElement("button");
        bar.type = "button";
        bar.className = "pc-rev__bar" + (state.starFilter === n ? " is-active" : "");
        bar.setAttribute("aria-pressed", state.starFilter === n ? "true" : "false");
        bar.setAttribute("aria-label", n + " star reviews: " + c + (state.starFilter === n ? " (filter active)" : ""));
        var lab = document.createElement("span");
        lab.textContent = n + " star";
        var track = document.createElement("span");
        track.className = "pc-rev__bar-track";
        var fill = document.createElement("span");
        fill.className = "pc-rev__bar-fill";
        fill.style.width = (s.review_count ? (c / s.review_count) * 100 : 0) + "%";
        track.appendChild(fill);
        var nEl = document.createElement("span");
        nEl.className = "pc-rev__bar-n";
        nEl.textContent = c;
        bar.appendChild(lab); bar.appendChild(track); bar.appendChild(nEl);
        bar.addEventListener("click", function () {
          state.starFilter = state.starFilter === n ? null : n;
          state.offset = 0;
          renderSummary(state.summary);
          renderFilterNote();
          loadPage(true);
        });
        bars.appendChild(bar);
      });
      els.summary.appendChild(bars);

      var aside = document.createElement("div");
      aside.className = "pc-rev__aside";
      if (s.pct_recommend !== null && s.pct_recommend !== undefined) {
        var rec = document.createElement("p");
        rec.className = "pc-rev__rec";
        var strong = document.createElement("strong");
        strong.textContent = s.pct_recommend + "%";
        rec.appendChild(strong);
        rec.appendChild(document.createTextNode(" of students would recommend this course"));
        aside.appendChild(rec);
      }
      var writeBtn = document.createElement("button");
      writeBtn.type = "button";
      writeBtn.className = "pc-btn pc-btn--primary pc-btn--md js-rev-write";
      writeBtn.textContent = "Write a review";
      aside.appendChild(writeBtn);
      els.summary.appendChild(aside);

      els.summary.hidden = false;
    }

    function renderFilterNote() {
      els.filterNote.textContent = "";
      if (!state.starFilter) return;
      els.filterNote.appendChild(document.createTextNode("Showing " + state.starFilter + "-star reviews"));
      var clear = document.createElement("button");
      clear.type = "button";
      clear.className = "pc-rev__filter-clear";
      clear.textContent = "Clear filter";
      clear.addEventListener("click", function () {
        state.starFilter = null;
        state.offset = 0;
        renderSummary(state.summary);
        renderFilterNote();
        loadPage(true);
      });
      els.filterNote.appendChild(clear);
    }

    /* ── Cards ── */
    function fmtDate(iso) {
      var d = new Date(iso + "T00:00:00");
      if (isNaN(d)) return "";
      var days = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (days <= 1) return "Today";
      if (days < 7) return days + " days ago";
      if (days < 45) {
        var w = Math.max(1, Math.round(days / 7));
        return w + (w === 1 ? " week ago" : " weeks ago");
      }
      return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
    function initials(name) {
      var parts = String(name).trim().split(/\s+/);
      var s = parts[0] ? parts[0][0] : "?";
      if (parts.length > 1) s += parts[parts.length - 1][0];
      return s.toUpperCase();
    }

    function card(r) {
      var li = document.createElement("li");
      li.className = "pc-rev__item";

      var head = document.createElement("div");
      head.className = "pc-rev__item-head";
      var av = document.createElement("span");
      av.className = "pc-rev__avatar";
      av.setAttribute("aria-hidden", "true");
      av.textContent = initials(r.author_name);
      head.appendChild(av);

      var who = document.createElement("div");
      who.className = "pc-rev__who";
      var name = document.createElement("span");
      name.className = "pc-rev__name";
      name.textContent = r.author_name;
      if (r.verified_student) {
        var badge = document.createElement("span");
        badge.className = "pc-rev__badge";
        badge.textContent = "Verified student";
        name.appendChild(badge);
      }
      who.appendChild(name);
      if (r.author_location) {
        var loc = document.createElement("span");
        loc.className = "pc-rev__loc";
        loc.textContent = r.author_location;
        who.appendChild(loc);
      }
      head.appendChild(who);

      var meta = document.createElement("div");
      meta.className = "pc-rev__item-meta";
      meta.appendChild(stars(r.rating));
      var date = document.createElement("span");
      date.className = "pc-rev__date";
      date.textContent = fmtDate(r.reviewed_at);
      meta.appendChild(date);
      head.appendChild(meta);
      li.appendChild(head);

      if (r.title) {
        var t = document.createElement("h3");
        t.className = "pc-rev__item-title";
        t.textContent = r.title;
        li.appendChild(t);
      }
      var body = document.createElement("p");
      body.className = "pc-rev__item-body";
      body.textContent = r.body;
      li.appendChild(body);

      var chips = [];
      if (r.package) chips.push(["Package", r.package]);
      if (r.attributes && typeof r.attributes === "object") {
        Object.keys(r.attributes).forEach(function (k) {
          var v = r.attributes[k];
          if (v === null || v === undefined || v === "") return;
          chips.push([ATTR_LABELS[k] || k.replace(/_/g, " "), String(v)]);
        });
      }
      if (chips.length) {
        var row = document.createElement("div");
        row.className = "pc-rev__chips";
        chips.forEach(function (c) {
          var chip = document.createElement("span");
          chip.className = "pc-rev__chip";
          var b = document.createElement("strong");
          b.textContent = c[0] + ": ";
          chip.appendChild(b);
          chip.appendChild(document.createTextNode(c[1]));
          row.appendChild(chip);
        });
        li.appendChild(row);
      }

      var foot = document.createElement("div");
      foot.className = "pc-rev__item-foot";
      foot.appendChild(document.createTextNode("Was this helpful?"));
      foot.appendChild(voteBtn(r, true));
      foot.appendChild(voteBtn(r, false));
      li.appendChild(foot);

      return li;
    }

    function voteBtn(r, helpful) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pc-rev__vote";
      var n = helpful ? r.helpful_yes : r.helpful_no;
      btn.textContent = (helpful ? "Yes" : "No") + (n ? " (" + n + ")" : "");
      var guard = "pcrv_" + r.id;
      var voted = null;
      try { voted = localStorage.getItem(guard); } catch (e) {}
      if (voted) {
        btn.disabled = true;
        if (voted === (helpful ? "y" : "n")) btn.classList.add("is-voted");
        return btn;
      }
      btn.addEventListener("click", function () {
        try { localStorage.setItem(guard, helpful ? "y" : "n"); } catch (e) {}
        var row = btn.parentElement;
        row.querySelectorAll(".pc-rev__vote").forEach(function (b) { b.disabled = true; });
        btn.classList.add("is-voted");
        btn.textContent = (helpful ? "Yes" : "No") + " (" + (n + 1) + ")";
        fetch(api + "/rest/v1/rpc/storefront_review_vote", {
          method: "POST",
          headers: { apikey: key, Authorization: "Bearer " + key, "Content-Type": "application/json" },
          body: JSON.stringify({ p_review_id: r.id, p_helpful: helpful })
        }).catch(function () {});
      });
      return btn;
    }

    /* ── Paging ── */
    function filteredCount() {
      var s = state.summary;
      if (!s) return 0;
      return state.starFilter ? (s["r" + state.starFilter] || 0) : s.review_count;
    }

    function loadPage(reset) {
      if (state.loading) return;
      state.loading = true;
      if (reset) els.list.setAttribute("aria-busy", "true");
      var q = "storefront_reviews_public?product_handle=eq." + encodeURIComponent(handle) +
        "&order=" + SORTS[state.sort] +
        "&limit=" + state.pageSize + "&offset=" + state.offset;
      if (state.starFilter) q += "&rating=eq." + state.starFilter;
      rest(q).then(function (rows) {
        if (reset) els.list.textContent = "";
        rows.forEach(function (r) { els.list.appendChild(card(r)); });
        state.offset += rows.length;
        els.more.hidden = state.offset >= filteredCount() || rows.length === 0;
      }).catch(function () {
        if (reset) els.list.textContent = "";
        els.more.hidden = true;
      }).finally(function () {
        state.loading = false;
        els.list.removeAttribute("aria-busy");
      });
    }

    /* ── Dialog / form ── */
    function openDialog() {
      els.formBody.hidden = false;
      els.formDone.hidden = true;
      els.formError.hidden = true;
      if (typeof els.dialog.showModal === "function") els.dialog.showModal();
      else els.dialog.setAttribute("open", "");
    }
    function closeDialog() {
      if (typeof els.dialog.close === "function") els.dialog.close();
      else els.dialog.removeAttribute("open");
    }

    root.addEventListener("click", function (e) {
      if (e.target.closest(".js-rev-write")) openDialog();
      if (e.target.closest(".js-rev-close")) closeDialog();
    });
    els.dialog.addEventListener("click", function (e) {
      if (e.target === els.dialog) closeDialog(); // backdrop click
    });

    els.form.addEventListener("submit", function (e) {
      e.preventDefault();
      els.formError.hidden = true;
      var fd = new FormData(els.form);

      if (fd.get("website")) { // honeypot: pretend success
        els.formBody.hidden = true;
        els.formDone.hidden = false;
        return;
      }
      var body = String(fd.get("body") || "").trim();
      var payload = {
        product_handle: handle,
        rating: parseInt(fd.get("rating"), 10),
        author_name: String(fd.get("author_name") || "").trim(),
        author_email: String(fd.get("author_email") || "").trim(),
        body: body
      };
      var title = String(fd.get("title") || "").trim();
      var loc = String(fd.get("author_location") || "").trim();
      var pkg = String(fd.get("package") || "").trim();
      var rec = fd.get("would_recommend");
      if (title) payload.title = title;
      if (loc) payload.author_location = loc;
      if (pkg) payload.package = pkg;
      if (rec === "true" || rec === "false") payload.would_recommend = rec === "true";

      var err = null;
      if (!payload.rating) err = "Please choose a star rating.";
      else if (!payload.author_name) err = "Please enter your name.";
      else if (!/.+@.+\..+/.test(payload.author_email)) err = "Please enter a valid email.";
      else if (body.length < 20) err = "Please write at least a couple of sentences (20+ characters).";
      if (err) {
        els.formError.textContent = err;
        els.formError.hidden = false;
        return;
      }

      var submitBtn = els.form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      fetch(api + "/rest/v1/storefront_reviews", {
        method: "POST",
        headers: {
          apikey: key, Authorization: "Bearer " + key,
          "Content-Type": "application/json", Prefer: "return=minimal"
        },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) throw new Error("submit " + r.status);
        els.formBody.hidden = true;
        els.formDone.hidden = false;
        els.form.reset();
      }).catch(function () {
        els.formError.textContent = "Something went wrong submitting your review. Please try again.";
        els.formError.hidden = false;
      }).finally(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
    });

    els.sort.addEventListener("change", function () {
      state.sort = els.sort.value in SORTS ? els.sort.value : "recent";
      state.offset = 0;
      loadPage(true);
    });
    if (els.moreBtn) els.moreBtn.addEventListener("click", function () { loadPage(false); });

    /* ── Boot ── */
    rest("storefront_review_summaries?product_handle=eq." + encodeURIComponent(handle)).then(function (rows) {
      var s = rows && rows[0];
      if (!s || !s.review_count) {
        state.summary = null;
        if (state.showEmpty) {
          els.empty.hidden = false;
          root.hidden = false;
        }
        return;
      }
      state.summary = s;
      renderSummary(s);
      els.toolbar.hidden = false;
      root.hidden = false;
      loadPage(true);
    }).catch(function () {
      /* API unreachable → leave section hidden; page is unaffected. */
    });
  }

  function boot() {
    document.querySelectorAll("[data-pc-reviews]").forEach(init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
